import { ChevronDown, ChevronRight, Code, Lock, SearchCode } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { cn } from "~/lib/utils";
import { HighlightedCode } from "~/pages/manifold-nextgen/components/conversations/HighlightedCode";
import type {
  RefreshRunStatus,
  StepState,
  WorkflowSpec,
  WorkflowStep,
} from "~/services/pinnedArtifacts";

export const RUN_STATUS_VARIANT: Record<
  RefreshRunStatus,
  "info" | "success" | "destructive" | "muted"
> = {
  pending: "muted",
  running: "info",
  succeeded: "success",
  failed: "destructive",
};

// Runtime ids name their language: `r-sandbox`, `omnitool-r`, `python-sandbox`.
const isRRuntime = (runtime: string | null) => !!runtime && /(^r-|-r$)/.test(runtime);

const stepLanguage = (step: WorkflowStep) =>
  step.kind === "sql" ? "sql" : isRRuntime(step.runtime) ? "r" : "python";

const stepBadge = (step: WorkflowStep) => {
  if (step.kind === "sql") {
    return {
      label: "SQL",
      icon: SearchCode,
      className: "border-teal-200 bg-teal-50 text-teal-700 dark:bg-teal-950/50",
    };
  }
  if (step.kind === "code") {
    return {
      label: isRRuntime(step.runtime) ? "R" : "Python",
      icon: Code,
      className: "border-amber-200 bg-amber-50 text-amber-700 dark:bg-amber-950/50",
    };
  }
  return { label: "Fixed", icon: Lock, className: "border-border text-muted-foreground" };
};

interface LineageStepsProps {
  spec: WorkflowSpec;
  openStep: number | null;
  onToggle: (index: number) => void;
  // Per-step progress of the refresh being followed, if any.
  stateByStep?: Map<string, StepState>;
}

/** Each step of the recipe, in the order a refresh runs them, expandable to
 *  the code it ran. */
export const LineageSteps = ({ spec, openStep, onToggle, stateByStep }: LineageStepsProps) => (
  <div className="flex flex-col gap-2.5">
    {spec.steps.map((step, index) => {
      const open = openStep === index;
      const badge = stepBadge(step);
      const state = stateByStep?.get(step.step_id);
      return (
        <div
          key={step.step_id}
          className={cn(
            "overflow-hidden rounded-lg border",
            open ? "border-ring/40" : "border-border"
          )}
        >
          <button
            type="button"
            onClick={() => onToggle(index)}
            aria-expanded={open}
            className={cn(
              "flex w-full cursor-pointer items-start gap-2.5 px-3 py-2.5 text-left hover:bg-muted/50",
              open && "bg-muted/50"
            )}
          >
            <span className="w-2.5 text-xs leading-5 text-muted-foreground">{index + 1}</span>
            <span
              className={cn(
                "flex w-[68px] shrink-0 items-center justify-center gap-1 rounded border px-1.5 text-[11px] font-medium leading-[18px]",
                badge.className
              )}
            >
              <badge.icon className="size-3" aria-hidden />
              {badge.label}
            </span>
            <span className="min-w-0 flex-1 text-[13px] font-medium leading-5">
              {step.summary ?? step.step_id}
            </span>
            {state && <Badge variant={RUN_STATUS_VARIANT[state.status]}>{state.status}</Badge>}
            {open ? (
              <ChevronDown className="mt-0.5 size-3.5 shrink-0 text-foreground/70" />
            ) : (
              <ChevronRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            )}
          </button>
          {/* Outside the disclosure: a failed refresh has to say which step
              broke without the reader hunting for it. */}
          {state?.error && (
            <p className="border-t border-border px-3 py-2 text-xs text-destructive">
              {state.error}
            </p>
          )}
          {open && (
            <div className="flex flex-col gap-2 border-t border-border px-3 pt-2.5 pb-3">
              {step.source_tables.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  From {step.source_tables.join(", ")}
                </p>
              )}
              {step.runtime && (
                <p className="text-xs text-muted-foreground">Runs in {step.runtime}</p>
              )}
              {step.frozen_reason && (
                <p className="text-xs text-muted-foreground">
                  Reused as-is — {step.frozen_reason}.
                </p>
              )}
              {step.code && (
                <HighlightedCode
                  code={step.code}
                  language={stepLanguage(step)}
                  className="max-h-64 overflow-auto rounded-md border border-border bg-muted/40 px-3 py-2.5 pr-9 text-[11px] leading-relaxed"
                />
              )}
            </div>
          )}
        </div>
      );
    })}
  </div>
);
